import { useEffect, useRef, useCallback } from 'react';

import Plausible from 'plausible-tracker';
import { useLocation } from 'react-router-dom';

// Initialize Plausible tracker
// The domain should match what's configured in your Plausible account
const plausible = Plausible({
  domain: 'ucml.dev',
  apiHost: 'https://plausible.stuart.fun',
  trackLocalhost: false,
});

// Extract the functions to avoid dependency changes causing rerenders
const { trackPageview, enableAutoOutboundTracking } = plausible;

/**
 * Custom hook to track page views with Plausible analytics
 * Automatically tracks page changes as the user navigates through the app
 */
const usePlausible = () => {
  const location = useLocation();
  const initialized = useRef(false);

  // Setup Plausible tracking when component mounts
  useEffect(() => {
    if (!initialized.current) {
      // Enable auto tracking of outbound links
      enableAutoOutboundTracking();
      initialized.current = true;
    }
  }, []);

  // Track page views on route changes
  useEffect(() => {
    // Send a pageview
    trackPageview({
      url: location.pathname + location.search,
    });
  }, [location]);

  // Memoize the track event function to prevent it from changing on every render
  const trackEvent = useCallback((eventName: string, props?: Record<string, string | number | boolean>) => {
    plausible.trackEvent(eventName, { props });
  }, []);

  // Return the Plausible instance for custom event tracking
  return {
    trackEvent
  };
};

export default usePlausible;